/**
 * Community — the pure, render-side rules: hrefs, tier and badge presentation, the small
 * sentences. No fetch, no React; everything here is unit-tested in
 * `tests/community-model.spec.ts`.
 *
 * What is NOT here: points, tier thresholds, ranks, founding numbers. DealHunt derives those
 * and sends them (see `types.ts`) — this module only words and links what it is given.
 */
import { noteDeepLink } from 'lib/owner-notes/band-copy'
import { hoursText } from 'lib/owner-notes/deltas'
import { AUDIO_NODE_SLUG } from 'lib/dealhunt/constants'
import { withSlash } from 'lib/dealhunt/url'
import type {
  CommunityBadgeId,
  CommunityCategoryNode,
  CommunityFeedFilter,
  CommunityFeedItem,
  CommunityVoteCounts,
  CommunityFeedSort,
  CommunityLeaderboardResponse,
  CommunityLeaderRow,
  CommunityMemberCardGear,
  CommunityMemberCardResponse,
  CommunityMemberResponse,
  CommunityPeriod,
  CommunityPoints,
  CommunityProgressStep,
  CommunityProduct,
  CommunityPublishStanding,
  CommunityFoundingMemberRule,
  CommunityStanding,
  CommunityTier,
} from './types'

// ── routes ────────────────────────────────────────────────────────────────────

export const COMMUNITY_PATH = '/community'
export const LEADERBOARD_PATH = '/community/leaderboard'
export const MEMBERS_PATH = '/community/members'
export const PRODUCTS_PATH = '/community/products'
/** The community review form (`pages/community/review/new.tsx`). */
export const NEW_REVIEW_PATH = '/community/review/new'

export function communityHref(trailingSlash: boolean): string {
  return withSlash(COMMUNITY_PATH, trailingSlash)
}

/** The period every leaderboard opens on; kept out of the URL by `leaderboardHref`. */
export const LEADERBOARD_DEFAULT_PERIOD: CommunityPeriod = 'all'

export function leaderboardHref(
  trailingSlash: boolean,
  opts: { period?: CommunityPeriod; category?: string | null } = {}
): string {
  const qs = new URLSearchParams()
  // The default period (All time, every board) stays out of the URL so each board has one address.
  if (opts.period && opts.period !== LEADERBOARD_DEFAULT_PERIOD) {
    qs.set('period', opts.period)
  }
  if (opts.category) {
    qs.set('category', opts.category)
  }
  const query = qs.toString()
  return withSlash(LEADERBOARD_PATH, trailingSlash) + (query ? `?${query}` : '')
}

/** The member hub's pages under `/community/members/<handle>`: the Overview (no segment), its
 *  tabs, and the Badges page reached from the Overview. Owner-only tabs (Notifications,
 *  Settings) join this union when their pages exist. */
export type MemberHubPage = 'overview' | 'gear' | 'stats' | 'notifications' | 'settings' | 'badges'

/**
 * Every member hub address is built here — `/community/members/<handle>[/<page>]`, the handle
 * lowercased (the pages 308 any other spelling to this one). A handle rename or a new tab
 * changes this function, not its callers.
 */
export function memberHubHref(
  handle: string,
  trailingSlash: boolean,
  page: MemberHubPage = 'overview'
): string {
  const base = `${MEMBERS_PATH}/${encodeURIComponent(handle.toLowerCase())}`
  return withSlash(page === 'overview' ? base : `${base}/${page}`, trailingSlash)
}

/** A member's Overview — `memberHubHref` without a page. */
export function memberHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash)
}

/** A member's Gear tab: `/community/members/<handle>/gear`. */
export function memberGearHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash, 'gear')
}

/** A member's Stats tab (public, Member Stats 2026-10-11): `/community/members/<handle>/stats`. */
export function memberStatsHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash, 'stats')
}

/** A member's Notifications tab (owner-only): `/community/members/<handle>/notifications`. */
export function memberNotificationsHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash, 'notifications')
}

/** A member's Settings tab (owner-only): `/community/members/<handle>/settings`. */
export function memberSettingsHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash, 'settings')
}

/**
 * Where a signed-in reader turns their public page on or off: Settings (Member Hub phase 5a).
 * The link is `/profile` itself — the redirector (`profileRoute`): a reader WITH a member page is
 * sent on to their hub's Settings tab, a hidden or unclaimed one gets the same Settings rendered
 * on `/profile` (their hub page 404s). The one builder for that link — the You card's
 * hidden-profile step and the leaderboard's strip (the hub's own "Public · change" links
 * `memberSettingsHref`). `profileHref` is the app's `profileLink.href`.
 */
export function publicProfileSettingsHref(profileHref: string): string {
  return profileHref
}

/**
 * The member path segment as DealHunt will look it up — lowercase — or null when it cannot be
 * a handle at all. Checked BEFORE any fetch: a made-up path must cost DealHunt nothing, and
 * every spelling of one handle must be one cached page (the page 308s to this form).
 */
export function memberHandleParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const handle = raw.replace(/^@/, '').toLowerCase()
  return /^[a-z0-9_]{3,18}$/.test(handle) ? handle : null
}

/** Only `https:` images are rendered: the field is DealHunt's, the `<img>` is ours. */
export function safeImageUrl(url: string | null | undefined): string | null {
  return typeof url === 'string' && /^https:\/\/[^\s]+$/.test(url) ? url : null
}

/** null when DealHunt could not build a slug (a node without a taxonomy path): the product is
 *  then named without a link rather than linked to a bare `/product/`. */
export function communityProductHref(
  product: CommunityProduct,
  trailingSlash: boolean
): string | null {
  return product.slug ? withSlash(`/product/${product.slug}`, trailingSlash) : null
}

/** A product's hub: every owner review on it, newest first. */
export function productHubHref(slug: string, trailingSlash: boolean): string {
  return withSlash(`${PRODUCTS_PATH}/${slug}`, trailingSlash)
}

/** One owner review's own page — "the thread". The note id is the stable key: a handle can be
 *  renamed once and released, a note id cannot. */
export function threadHref(slug: string, noteId: string, trailingSlash: boolean): string {
  return withSlash(`${PRODUCTS_PATH}/${slug}/${noteId}`, trailingSlash)
}

/** Where a feed row's sentence leads: that review's thread. null without a product slug. */
export function reviewHref(item: CommunityFeedItem, trailingSlash: boolean): string | null {
  return item.product.slug ? threadHref(item.product.slug, item.note.id, trailingSlash) : null
}

/** The same review's card on the product page — the thread's "See it on the product page". */
export function productNoteHref(item: CommunityFeedItem, trailingSlash: boolean): string | null {
  const href = communityProductHref(item.product, trailingSlash)
  return href ? noteDeepLink(href, item.note.id) : null
}

/** The community review form for one product: `/community/review/new?product=<slug>` (the
 *  trailing slash, when the app uses one, goes before the query). */
export function newReviewHref(slug: string, trailingSlash: boolean): string {
  return `${withSlash(NEW_REVIEW_PATH, trailingSlash)}?product=${encodeURIComponent(slug)}`
}

/** Where "I own it" leads: the community review form for that product. */
export function writeReviewHref(product: CommunityProduct, trailingSlash: boolean): string | null {
  return product.slug ? newReviewHref(product.slug, trailingSlash) : null
}

/** The SoundGuys product slug of a community URL, or null when it cannot be one. Checked
 *  BEFORE any fetch, like `memberHandleParam`; the page 308s other spellings to this one. */
export function productSlugParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const slug = raw.toLowerCase()
  return /^[a-z0-9-]{1,120}$/.test(slug) ? slug : null
}

/** A note id (a UUID) in a thread URL, lowercased, or null. */
export function noteIdParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const id = raw.toLowerCase()
  return /^[0-9a-f-]{36}$/.test(id) ? id : null
}

/** The slug in a stored declaration's SoundGuys href (`/product/<slug>/`), or null. */
export function slugFromProductHref(href: string | null): string | null {
  const match = href ? /^\/product\/([^/?#]+)\/?$/.exec(href) : null
  return match ? productSlugParam(match[1]) : null
}

/**
 * The taxonomy node id of a typeahead hit. Node records in `products_v2` carry it as
 * `objectID = family_<node id>` — the rule DealHunt's own search route reads them with
 * (`enrichNodeHits`). A raw product record has no node id: null, and the row shows no count.
 */
export function hitNodeId(hit: { objectID: string }): string | null {
  return hit.objectID.startsWith('family_') ? hit.objectID.slice('family_'.length) || null : null
}

// ── tiers ─────────────────────────────────────────────────────────────────────

/** Reader-facing words for DealHunt's tier ids (`ownerCommunityTypes.ts`). The ids and thresholds
 *  do not change; the words moved on 2026-10-17 (team: "Expert" at 600 pts, ~8–12 reviews, was
 *  premature): id `listener` (0 pts) reads "Member", id `expert` (600 pts) reads "Listener". A
 *  DealHunt comment saying "the Listener tier" means the id, i.e. "Member" on the page. */
export const TIER_LABELS: Readonly<Record<CommunityTier, string>> = {
  listener: 'Member',
  contributor: 'Contributor',
  expert: 'Listener',
  master: 'Master',
  reference: 'Reference',
}

/** A tier id this build does not know (a newer DealHunt) reads as the entry tier, never as
 *  "undefined". */
export function knownTier(tier: string): CommunityTier {
  return Object.prototype.hasOwnProperty.call(TIER_LABELS, tier)
    ? (tier as CommunityTier)
    : 'listener'
}

/** "a Listener", "a Contributor". */
export function withArticle(word: string): string {
  return `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`
}

export function tierLabel(tier: string): string {
  return TIER_LABELS[knownTier(tier)]
}

/** The one-line "how you get there" under each tier card. Points only in phase 1 — the
 *  thresholds are DealHunt's (`tiers[].min_points`), the words are ours. */
export function tierRequirement(tier: CommunityTier, minPoints: number): string {
  const pts = `${minPoints.toLocaleString('en-US')} pts`
  switch (tier) {
    case 'listener':
      return `${pts} · signed in`
    case 'contributor':
      return `${pts} · first owner review`
    case 'reference':
      return `${pts} · invited by editors`
    default:
      return pts
  }
}

export function membersLine(count: number): string {
  return count === 1 ? '1 member' : `${count.toLocaleString('en-US')} members`
}

// ── avatar ────────────────────────────────────────────────────────────────────

/** Soft disc fills, light enough for any tier ring. Members have no picture in phase 1, so a
 *  handle always maps to the same fill — the disc is an identity cue, not decoration. */
export const AVATAR_FILLS = [
  '#c9b8ff',
  '#bfe0fb',
  '#ffd8b5',
  '#d5f0dc',
  '#f6e7a8',
  '#e6e8eb',
  '#fde0e0',
  '#dfe3ff',
] as const

export function avatarFill(handle: string): string {
  let hash = 0
  for (const ch of handle.toLowerCase()) {
    hash = (hash * 31 + ch.charCodeAt(0)) % 2147483647
  }
  return AVATAR_FILLS[hash % AVATAR_FILLS.length]
}

/** Google-style initials: from the member's shown name when there is one (`Zoë García` → "ZG",
 *  the first letter of its first two words that start with a letter or digit — an emoji word is
 *  skipped), else from the handle: `maroon_glacier_71` → "MG", `humble` → "H", numeric parts
 *  (the handle's suffix) dropped, nothing left → "?". Code-point safe. The ONE initials helper —
 *  the owner-note marks (PickOwners) and the @mention picker read it too (identity ticket §3.3). */
export function avatarInitials(handle: string, name?: string | null): string {
  const words = (name ?? '').split(/\s+/).filter(word => /^[\p{L}\p{N}]/u.test(word))
  if (words.length) {
    return words
      .slice(0, 2)
      .map(word => Array.from(word)[0].toUpperCase())
      .join('')
  }
  const parts = handle.split(/[_\-.\s]+/).filter(part => part !== '' && !/^\d+$/.test(part))
  const letters = parts.slice(0, 2).map(part => Array.from(part)[0].toUpperCase())
  return letters.length ? letters.join('') : '?'
}

/** A Google photo at `px` (pass 2× the drawn size for sharp discs): Google's avatar URLs end in
 *  a size suffix (`…=s96-c`), rewritten to `=s<px>-c`. Any other URL is returned as is — never
 *  guessed at (identity ticket §3.3). */
export function googlePhotoUrl(src: string, px: number): string {
  return src.replace(/=s\d+-c$/, `=s${Math.max(1, Math.round(px))}-c`)
}

/** The reader's own identity facts from `/me` (DealHunt `identity`, contract B.3) — structural,
 *  so this reads `OwnerMeResponse.identity` without depending on when its type lands. */
export interface OwnIdentityFacts {
  display_name: string | null
  avatar_url: string | null
  show_identity: boolean
  acknowledged: boolean
}

/** What the reader's OWN surfaces (You card, review preview, reward screen) draw for them. */
export interface OwnShownIdentity {
  name: string
  avatarUrl: string | null
}

/**
 * The reader's own name and photo as the community shows them (contract B.3): only when they
 * show it AND acknowledged it (an existing member who has not answered the banner still reads as
 * the pseudonym — the consent gate) AND there is a name. Otherwise null: today's handle. A photo
 * never shows without the name. Own surfaces only — hydrated from `/me`, never the cached HTML.
 */
export function ownShownIdentity(
  me:
    | {
        profile: { claimed: boolean }
        community?: { profile_public: boolean } | null
        identity?: OwnIdentityFacts | null
      }
    | null
    | undefined
): OwnShownIdentity | null {
  // DealHunt's resolver serves a name only for a claimed, public profile (E.4): the reader's own
  // surfaces show what the community shows, so a hidden profile reads as its handle here too.
  const identity =
    me?.profile.claimed && me.community?.profile_public !== false ? me.identity : null
  if (!identity || !identity.show_identity || !identity.acknowledged) {
    return null
  }
  const name = typeof identity.display_name === 'string' ? identity.display_name.trim() : ''
  if (!name) {
    return null
  }
  return { name, avatarUrl: identity.avatar_url || null }
}

// ── time ──────────────────────────────────────────────────────────────────────

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** "5h", "2d", "3w", then a month and year. `now` is the page's server timestamp (`ts`) so
 *  the server and the first client render print the same string. */
export function shortAge(iso: string, now: number): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) {
    return ''
  }
  const diff = Math.max(0, now - then)
  if (diff < HOUR) {
    return `${Math.max(1, Math.floor(diff / MINUTE))}m`
  }
  if (diff < DAY) {
    return `${Math.floor(diff / HOUR)}h`
  }
  if (diff < 7 * DAY) {
    return `${Math.floor(diff / DAY)}d`
  }
  if (diff < 35 * DAY) {
    return `${Math.floor(diff / (7 * DAY))}w`
  }
  return monthYear(iso)
}

export function monthYear(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** "October" from DealHunt's "2026-10". */
export function monthName(month: string): string {
  const date = new Date(`${month}-01T00:00:00Z`)
  if (Number.isNaN(date.getTime())) {
    return 'This month'
  }
  return date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
}

// ── feed row ──────────────────────────────────────────────────────────────────

export interface ReadChip {
  id: string
  /** "Fit · better" — the chip text of the thread and the writer's preview. */
  text: string
  /** "Fit" — the read alone: the feed row's reads line draws the tone as a ✓ / ✕ glyph. */
  label: string
  tone: 'better' | 'worse'
}

/** The departure chips of a feed row: only reads that left "as expected" — the same rule as
 *  the product page's chips, where an expected read says nothing worth a chip. */
export function readChips(item: CommunityFeedItem): ReadChip[] {
  return (
    item.labels.reads
      // An empty label is a read the author's question set no longer names: no chip.
      .filter(r => r.label && (r.value === 'better' || r.value === 'worse'))
      .map(r => ({
        id: r.id,
        text: `${r.label} · ${r.value}`,
        label: r.label,
        tone: r.value as 'better' | 'worse',
      }))
  )
}

/** "24 owner reviews →" beside a feed row's product name: the product's threads page. null while
 *  DealHunt does not send the product's count (`product.review_count`, plan slice 3) — the name
 *  and thumb still link there, nothing is guessed. */
export function productHubHint(product: Pick<CommunityProduct, 'review_count'>): string | null {
  const count = product.review_count
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 1) {
    return null
  }
  return count === 1 ? '1 owner review →' : `${count.toLocaleString('en-US')} owner reviews →`
}

export function buyAgainChip(item: CommunityFeedItem): { text: string; yes: boolean } {
  const yes = item.note.answers.buy_again
  return { text: yes ? 'Would buy again' : 'Wouldn’t buy again', yes }
}

export function gearLine(row: CommunityLeaderRow): string {
  const tier = tierLabel(row.tier)
  if (!row.gear) {
    return tier
  }
  return `${tier} · ${row.gear.name}${row.gear.more > 0 ? ` +${row.gear.more}` : ''}`
}

// ── founding 100 ──────────────────────────────────────────────────────────────

export function spotsLeft(founding: { count: number; cap: number }): number {
  return Math.max(0, founding.cap - founding.count)
}

export function foundingTotals(reviews: number, products: number): string {
  const r = reviews === 1 ? '1 owner review' : `${reviews.toLocaleString('en-US')} owner reviews`
  const p = products === 1 ? '1 product' : `${products.toLocaleString('en-US')} products`
  return `${r} across ${p}`
}

// ── badges ────────────────────────────────────────────────────────────────────

/** What earns a badge — the section it sits in on the Badges page and the eyebrow of its
 *  popover. A new badge slots into one of these, so the layout never changes shape (design 2c).
 *  `limited` holds the badges DealHunt numbers and caps: once the numbers are gone, nobody earns
 *  them. The scarcity itself (counts, caps) is always the summary's, never this list's. */
export type BadgeGroup = 'limited' | 'reviews' | 'conversations' | 'milestones'

/** The sections in page order, with their headings. */
export const BADGE_GROUPS: ReadonlyArray<{ id: BadgeGroup; label: string }> = [
  { id: 'limited', label: 'Limited' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'conversations', label: 'Conversations' },
  { id: 'milestones', label: 'Milestones' },
]

export function badgeGroupLabel(group: BadgeGroup): string {
  return BADGE_GROUPS.find(g => g.id === group)?.label ?? ''
}

/** A badge's SoundGuys-side definition: the one record per id. Name, glyph and tone draw it;
 *  `group` places it; `meaning` is WHY the badge exists, in words with NO numbers — the rule and
 *  every threshold are DealHunt's and reach the reader only through `progress` / `detail`. */
export interface BadgeLook {
  name: string
  glyph: string
  /** Suffix of the CSS classes `.badge_<tone>` in Badges.module.css. */
  tone: 'ink' | 'blue' | 'violet' | 'green' | 'gold' | 'orange'
  group: BadgeGroup
  meaning: string
}

/** The record's key order is the definition order: the tie-break wherever badges are ranked
 *  (`closestBadges`) and the order inside a Badges-page section. */
export const BADGE_LOOKS: Readonly<Record<CommunityBadgeId, BadgeLook>> = {
  // Early Owner's hexagon prints DealHunt's cap ("first 100"), never a literal: `badgeGlyph`
  // draws it at the hexagon sites; this digit-free mark is what shows when no cap was sent.
  founding_owner: {
    name: 'Early Owner',
    glyph: '◇',
    tone: 'ink',
    group: 'limited',
    meaning:
      'For the first owners who signed in and reviewed their gear here. It can’t be earned once the spots are gone.',
  },
  founding_member: {
    name: 'Founding Member',
    glyph: '✦',
    tone: 'ink',
    group: 'limited',
    meaning: 'For the members who built the first owner reviews and conversations.',
  },
  // The two Early Discussions badges are capped and numbered like the founding two, so they sit
  // in Limited (their scarcity is the thing to know), not in Conversations.
  conversation_starter: {
    name: 'Conversation Starter',
    glyph: '❝',
    tone: 'ink',
    group: 'limited',
    meaning: 'For the first discussions that got the community talking.',
  },
  first_responder: {
    name: 'First Responder',
    glyph: '⚡',
    tone: 'orange',
    group: 'limited',
    meaning: 'For the first answers other readers marked helpful.',
  },
  first_review: {
    name: 'First Review',
    glyph: '✎',
    tone: 'blue',
    group: 'reviews',
    meaning: 'For sharing what owning your gear is really like.',
  },
  review_trio: {
    name: 'Review Trio',
    glyph: '3',
    tone: 'violet',
    group: 'reviews',
    meaning: 'For owners who keep coming back to review more of their gear.',
  },
  six_month_update: {
    name: 'Six-Month Update',
    glyph: '↻',
    tone: 'gold',
    group: 'reviews',
    meaning: 'For going back to a review once time has shown how the gear holds up.',
  },
  cross_category: {
    name: 'Cross-Category',
    glyph: '◎',
    tone: 'orange',
    group: 'reviews',
    meaning: 'For owners whose reviews cover more than one kind of gear.',
  },
  first_reply: {
    name: 'First Reply',
    glyph: '↩',
    tone: 'blue',
    group: 'conversations',
    meaning: 'For joining the conversation on another owner’s review or discussion.',
  },
  long_haul_owner: {
    name: 'Long-Haul Owner',
    glyph: '⧗',
    tone: 'green',
    group: 'milestones',
    meaning: 'For reviews written after living with a product for a long time.',
  },
  helpful_10: {
    name: 'Helpful',
    glyph: '▲',
    tone: 'gold',
    group: 'milestones',
    meaning: 'For reviews other readers found genuinely useful.',
  },
  top3_month: {
    name: 'Top 3 Month',
    glyph: '★',
    tone: 'gold',
    group: 'milestones',
    meaning: 'For the members who did the most for the community this month.',
  },
}

/** Definition order, for ranking tie-breaks: `founding_owner` 0, `founding_member` 1, … */
const BADGE_ORDER: ReadonlyMap<string, number> = new Map(
  Object.keys(BADGE_LOOKS).map((id, index) => [id, index])
)

export function badgeOrder(id: string): number {
  return BADGE_ORDER.get(id) ?? BADGE_ORDER.size
}

/** The two capped Early Discussions badges (the discussions hero's cards, `earlyBadgeCards`). */
export type EarlyBadgeKind = 'conversation_starter' | 'first_responder'

/** What a plain hexagon prints — the glyph picker for the three hexagon sites (member grid,
 *  member card, thread side; Directive 34aae964): Early Owner prints DealHunt's cap
 *  (`earlyOwnerCap`), or its digit-free mark without one; every other badge its look's glyph. */
export function badgeGlyph(
  badge: { id: CommunityBadgeId; detail?: string },
  foundingCap?: number | null
): string {
  if (badge.id === 'founding_owner') {
    const cap = earlyOwnerCap(foundingCap, badge.detail)
    if (cap !== null) {
      return cap.toLocaleString('en-US')
    }
  }
  return BADGE_LOOKS[badge.id].glyph
}

/** The Early Owner cap to print: the summary's `founding.cap` when the page has it, else the cap
 *  in DealHunt's own badge line ("#3 of 100", "85 of 100 left"), else null — never a literal. */
export function earlyOwnerCap(
  foundingCap: number | null | undefined,
  detail: string | undefined
): number | null {
  const sent = sentCount(foundingCap)
  if (sent !== null && sent > 0) {
    return sent
  }
  return typeof detail === 'string' ? badgeCap(detail) : null
}

/** A badge id this build has no look for (a newer DealHunt) is dropped, never rendered blank;
 *  a repeated id is shown once. */
export function knownBadges<T extends { id: string }>(
  badges: T[]
): Array<T & { id: CommunityBadgeId }> {
  const seen = new Set<string>()
  return badges
    .filter((b): b is T & { id: CommunityBadgeId } => {
      if (!Object.prototype.hasOwnProperty.call(BADGE_LOOKS, b.id) || seen.has(b.id)) {
        return false
      }
      seen.add(b.id)
      return true
    })
    .sort((a, b) => foundingRank(a.id) - foundingRank(b.id))
}

/** Early Owner, then Founding Member, then the two Early Discussions badges (Conversation
 *  Starter, First Responder — the capped ones), then the rest in DealHunt's order (a stable sort). */
const FOUNDING_RANK: Readonly<Record<string, number>> = {
  founding_owner: 0,
  founding_member: 1,
  conversation_starter: 2,
  first_responder: 3,
}

export function foundingRank(id: string): number {
  return FOUNDING_RANK[id] ?? 4
}

// ── replies and Helpful (phase 2) ────────────────────────────────────────────

/** A count as DealHunt sent it — a whole number ≥ 0 — or null for anything else (an older
 *  DealHunt, a negative, a string): "unknown", never zero, never clamped into a figure. */
export function sentCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
}

/** Visible replies on the review's thread (on the feed item — DealHunt
 *  `OwnerCommunityFeedItem.reply_count`), or null when DealHunt does not say. */
export function replyCount(item: CommunityFeedItem): number | null {
  return sentCount(item.reply_count)
}

/** Helpful votes on the review (public), or null when DealHunt does not say — no control then.
 *  DealHunt sends it on the item and on the note (the same figure); either will do. */
export function helpfulCount(item: CommunityFeedItem): number | null {
  return sentCount(item.helpful_count) ?? sentCount(item.note.helpful_count)
}

/** The review's public Yes / No counts (contract #201), or null when DealHunt does not send them
 *  — a DealHunt before the vote's value, where the feed row keeps the Helpful button (a "No"
 *  sent to it would be stored as a Helpful). */
export function voteCounts(item: CommunityFeedItem): CommunityVoteCounts | null {
  const counts = item.vote_counts
  if (!counts || typeof counts !== 'object') {
    return null
  }
  const yes = sentCount(counts.yes)
  const no = sentCount(counts.no)
  return yes === null || no === null ? null : { yes, no }
}

/** "3 replies", "1 reply", "No replies yet · be the first". */
export function replyCountLine(count: number): string {
  if (count <= 0) {
    return 'No replies yet · be the first'
  }
  return count === 1 ? '1 reply' : `${count.toLocaleString('en-US')} replies`
}

/** The ask under a review nobody has replied to (design 4a's dashed box and 5d's panel):
 *  the author is named, and the two things a reply can be. */
export function replyAskLine(handle: string): string {
  return `No replies yet. Ask ${handle} something, or share your own experience.`
}

/** The reply box's field prompt (design 5d). */
export function replyPlaceholder(handle: string): string {
  return `Ask ${handle} something, or share your own experience…`
}

/**
 * The reply box's note (2026-10-09). The author is promised a notification only when they are a
 * member (`author.member`, page props — the same for every reader): commentHub follows a CLAIMED
 * author into their thread when DealHunt registers it, and an anonymous one has no account to
 * notify. Otherwise the note promises only what is true for the replier — commentHub follows
 * whoever posts (`autoFollowThread`) — never a duration, never points.
 */
export function replyNoteLine(handle: string, authorNotified: boolean): string {
  return authorNotified
    ? `${handle} gets notified when you reply.`
    : `${handle} may not see replies yet — you’re notified when they answer.`
}

/** "+10 pts" — what a reply earns, from DealHunt's points table; null while the table has no
 *  `reply` (phase 1 DealHunt): nothing is promised. The cap is in the points table's fine print. */
export function replyPointsChip(
  points: Pick<CommunityPoints, 'reply'> | null | undefined
): string | null {
  return points && typeof points.reply === 'number' && points.reply > 0
    ? `+${points.reply.toLocaleString('en-US')} pts`
    : null
}

/**
 * The reply box's title (design 5d): "Be the first to reply" while the thread has none, else
 * "Reply". commentHub's total (the list the reader sees) wins once it has loaded; before, DealHunt's
 * `reply_count` (minutes behind commentHub) stands in, and an unknown count reads as none.
 */
export function replyBoxTitle(total: number | null, replies: number | null): string {
  const count = total ?? replies ?? 0
  return count > 0 ? 'Reply' : 'Be the first to reply'
}

/** The id the thread's replies section carries — a feed row's reply line jumps to it. */
export const REPLIES_ANCHOR = 'replies'

/**
 * "Reply →" under an owner review outside /community (the PDP wall, the card Owners block, a
 * guide's roster, the review post's card): the review's thread, at its reply box. No surface
 * there carries DealHunt's `reply_count`, so the link names no count — "Reply first" would claim
 * nobody has replied. null without a thread address.
 */
export const REPLY_LINK_TEXT = 'Reply'

export function replyLinkHref(thread: string | null): string | null {
  return thread ? `${thread}#${REPLIES_ANCHOR}` : null
}

/** The name commentHub's widget gives a replier it has no name for — the preview says the same. */
const NAMELESS_REPLIER = 'Anonymous'
/** A replier DealHunt sends with no name and no handle (#213 follow-up, ac20038b): a claimed
 *  profile that keeps the pseudonym, is hidden or blocked — membership only, never commentHub's
 *  name. The label hidden members get elsewhere (`HIDDEN_MEMBER_NAME`). */
const NEUTRAL_REPLIER = 'Member'

/** One reply under a feed row (feed plan §2 "Reply preview", contract #210), ready to draw. */
export interface ReplyPreviewView {
  /** The shown name: a member's name, or commentHub's for a non-member; null for a member who
   *  keeps the pseudonym (then the handle alone, as the row's author line draws it). */
  name: string | null
  /** The member's handle (display form); null for a commentHub-only replier. */
  handle: string | null
  /** The line as text ("Name @handle", "handle", "Name") — the avatar's accessible name. */
  label: string
  /** What the avatar's fill and initials derive from: the handle, else the name. */
  avatarKey: string
  initials: string
  /** `https:` only (`safeImageUrl`, Constraint 5efc0954), else null — the initials disc. */
  avatarUrl: string | null
  /** "SOUNDGUYS TEAM" beats "Also owns it"; the review's own author never wears "Also owns it". */
  pill: 'team' | 'owns' | null
  age: string
  text: string
  /** "2 replies →", "1 reply →". */
  repliesLabel: string
  /** The review's thread at its replies; null without a thread address (no product slug). */
  href: string | null
}

/** The pill's words for an owner who is not the review's author (mockup board 1). */
export const ALSO_OWNS_PILL = 'Also owns it'

/**
 * The reply preview under a feed row, or null when there is nothing to show: DealHunt sent no
 * `reply_preview` (no reply, commentHub unread, an older DealHunt), its count is 0 or its text is
 * empty. Never a "no replies yet" — an empty thread renders nothing there (plan §2). Public: every
 * field comes from the shared-cached item, nothing from the reader.
 */
export function replyPreviewModel(
  item: CommunityFeedItem,
  now: number,
  trailingSlash: boolean
): ReplyPreviewView | null {
  const preview = item.reply_preview
  if (!preview || typeof preview !== 'object' || !preview.author) {
    return null
  }
  const count = sentCount(preview.reply_count)
  const text = typeof preview.text === 'string' ? preview.text.trim() : ''
  if (!count || !text) {
    return null
  }
  const { author } = preview
  const handle = typeof author.handle === 'string' && author.handle ? author.handle : null
  const shown =
    typeof author.display_name === 'string' && author.display_name.trim()
      ? author.display_name.trim()
      : null
  // A member who keeps the pseudonym: the handle alone. A commentHub-only replier: their name.
  // No name and no handle (`display_name: null`): the neutral "Member"; an empty name from
  // commentHub: its own "Anonymous".
  let name: string | null = shown
  if (!handle && !shown) {
    name = typeof author.display_name === 'string' ? NAMELESS_REPLIER : NEUTRAL_REPLIER
  }
  let label = name as string
  if (handle) {
    label = name ? `${name} @${handle}` : handle
  }
  const ownReview =
    handle !== null &&
    memberHandleParam(handle) !== null &&
    memberHandleParam(handle) === memberHandleParam(item.author.handle)
  let pill: ReplyPreviewView['pill'] = null
  if (author.staff === true) {
    pill = 'team'
  } else if (author.owns === true && !ownReview) {
    pill = 'owns'
  }
  const avatarKey = handle ?? (name as string)
  const thread = reviewHref(item, trailingSlash)
  return {
    name,
    handle,
    label,
    avatarKey,
    initials: avatarInitials(avatarKey, name),
    avatarUrl: safeImageUrl(author.avatar_url),
    pill,
    age: shortAge(preview.created_at, now),
    text,
    repliesLabel: `${replyCountLine(count)} →`,
    href: thread ? `${thread}#${REPLIES_ANCHOR}` : null,
  }
}

/**
 * The one line after a successful claim anywhere in the owner journey (the band's accept or held
 * result, the writer's reward screen, the return card's "Keep it yours"): the author's thread
 * when it is live and known, else the community home.
 */
export const POST_CLAIM_INVITE = 'Join the conversation in Community →'

export function postClaimInviteHref(thread: string | null, trailingSlash: boolean): string {
  return thread ?? communityHref(trailingSlash)
}

/** A thread is indexable once it has a reply (Directive f401bc59: noindex until the server can
 *  see replies — DealHunt's `reply_count` is that signal). An unknown count stays noindex. */
export function threadIndexable(item: CommunityFeedItem): boolean {
  return (replyCount(item) ?? 0) > 0
}

/** commentHub's container for a review's thread — what `CommunityShell` mounts `<Comments />` on. */
export function threadContainerId(noteId: string): string {
  return `owner-review:${noteId}`
}

// "How points work" (`pointsGuideRows`, `pointsFinePrint`) lives in `gamification.ts`.

/** The leaderboard's Helpful column is shown only when DealHunt sends the figure. */
export function hasHelpfulColumn(rows: CommunityLeaderRow[]): boolean {
  return rows.some(row => sentCount(row.helpful) !== null)
}

// ── feed lists (phase 2) ──────────────────────────────────────────────────────

/** The Latest list: newest first only (`new`, the server-rendered default, #54 sentiment-blind),
 *  or the reviews without a reply (`unanswered`, the home's "Be the first to reply" pill — the
 *  reader's choice, asked from the browser, never in the URL or the HTML). "Best" is gone
 *  (2026-10-18). */
export type FeedTab = 'new' | 'unanswered'

/** What each list asks DealHunt: always newest first. */
export function feedTabQuery(tab: FeedTab): {
  sort: CommunityFeedSort
  filter: CommunityFeedFilter | null
} {
  return { sort: 'new', filter: tab === 'unanswered' ? 'unanswered' : null }
}

// ── feed paging ───────────────────────────────────────────────────────────────

/** "Show more": offset paging over a list that grows at the top, so a review published between
 *  two pages comes back twice — keep the first copy. The home feed and a product's hub. */
export function appendFeed<T extends { items: CommunityFeedItem[] }>(prev: T, next: T): T {
  const have = new Set(prev.items.map(item => item.note.id))
  return { ...next, items: [...prev.items, ...next.items.filter(i => !have.has(i.note.id))] }
}

// ── the reader's own card ─────────────────────────────────────────────────────

export type YouState =
  /** No device, or not signed in with nothing published. */
  | { kind: 'listener' }
  /** Published reviews, not signed in: no rank, no page, no founding number yet. */
  | { kind: 'unclaimed'; handle: string; reviews: number; spotsLeft: number | null }
  /** Signed in, public profile switched off: no rank, no page. */
  | { kind: 'hidden'; handle: string; standing: CommunityStanding }
  /** Signed in (claimed) with a public profile: a member, with a page and a place on the boards
   *  — review or not (Guillaume, 2026-10-04: owner reviews are one way to score, not the entry).
   *  `reviews` is the reader's published count (the You card's action); `standing` is null only
   *  against a DealHunt that predates it. */
  | { kind: 'member'; handle: string; reviews: number; standing: CommunityStanding | null }

export function youState(
  me: {
    profile: { handle: string; claimed: boolean }
    shelf: Array<{ note: { status: string } }>
    community?: CommunityStanding
    /** Read by `ownShownIdentity`, not here — typed so `/me` carries it through to the menu. */
    identity?: OwnIdentityFacts | null
  } | null
): YouState {
  if (!me) {
    return { kind: 'listener' }
  }
  const reviews = me.shelf.filter(s => s.note.status === 'published').length
  const { handle } = me.profile
  const standing = me.community ?? null
  if (!me.profile.claimed) {
    // Not signed in: a Listener, or an owner whose reviews wait for a sign-in to count.
    return reviews === 0
      ? { kind: 'listener' }
      : { kind: 'unclaimed', handle, reviews, spotsLeft: standing?.founding_spots_left ?? null }
  }
  if (standing && !standing.profile_public) {
    return { kind: 'hidden', handle, standing }
  }
  // Signed in with a public profile = a member, with or without a review (2026-10-04).
  return { kind: 'member', handle, reviews, standing }
}

/** Shared by the cards that say it: the pages are cached for a few minutes. */
export const BOARD_DELAY_LINE = 'The community pages catch up within a few minutes.'

/** The reader's own line under their name on the community cards. */
export function youLine(state: YouState): string {
  switch (state.kind) {
    case 'member': {
      const next = state.standing?.next_tier
      if (!state.standing) {
        return `Your owner reviews, replies and Helpful votes count on the leaderboard. ${BOARD_DELAY_LINE}`
      }
      return next
        ? `${next.points_to_go.toLocaleString('en-US')} points to ${tierLabel(next.id)}.`
        : 'You are at the top tier points can reach.'
    }
    case 'hidden':
      return 'Your public profile is off, so you are not on the leaderboard. Turn it on in Settings.'
    case 'unclaimed':
      return `Your ${
        state.reviews === 1 ? 'owner review is' : `${state.reviews} owner reviews are`
      } published. Sign in to get on the leaderboard${foundingClaimClause(state.spotsLeft)}.`
    default:
      return `One owner review gets you to ${TIER_LABELS.contributor}.`
  }
}

/** " and become an Early Owner (53 spots left)" — only while there are spots and we know how many. */
export function foundingClaimClause(left: number | null | undefined): string {
  if (!left || left <= 0) {
    return ''
  }
  return ` and become an Early Owner (${left} spots left)`
}

// `foundingSignInReason` (the keep prompt's Early Owner sentence) lives in `gamification.ts`.

/**
 * What an accepted publish earned, said right after "Published": the founding number when this
 * publish took one, the points, the tier it reached. For an owner who is not signed in: what
 * signing in would add. Empty array = say nothing (an older DealHunt, a plain edit).
 */
export function publishStandingLines(
  standing: CommunityPublishStanding | CommunityStanding | null | undefined,
  signedIn: boolean
): string[] {
  if (!standing) {
    return []
  }
  if (!signedIn) {
    const clause = foundingClaimClause(standing.founding_spots_left)
    return [`Sign in to put this owner review on the community leaderboard${clause}.`]
  }
  const lines: string[] = []
  const earned = 'points_awarded' in standing ? standing : null
  if (earned?.founding_new && earned.founding_number !== null) {
    lines.push(`You’re Early Owner #${earned.founding_number} — a badge nobody can earn later.`)
  }
  if (earned && earned.points_awarded > 0) {
    const reached = earned.tier_reached
      ? ` You’re now ${withArticle(tierLabel(earned.tier_reached))}.`
      : ''
    lines.push(
      `+${earned.points_awarded} points — ${earned.points.toLocaleString(
        'en-US'
      )} in all.${reached}`
    )
  }
  if (!earned && standing.founding_number !== null) {
    // After a claim: the standing the sign-in left the reader with.
    lines.push(`You’re Early Owner #${standing.founding_number} — a badge nobody can earn later.`)
  }
  if (lines.length && !standing.member) {
    lines.push('Your public profile is off, so this stays between us.')
  } else if (lines.length) {
    lines.push(BOARD_DELAY_LINE)
  }
  return lines
}

/** Progress toward the next tier, 0–100, for the bar under a member's points. */
export function tierProgress(points: number, pointsToGo: number | null): number {
  if (pointsToGo === null) {
    return 100
  }
  const target = points + pointsToGo
  return target <= 0 ? 0 : Math.min(100, Math.round((points / target) * 100))
}

// ── review counts and points (threads, hubs, the picker) ─────────────────────

export function reviewCountLine(count: number): string {
  return count === 1 ? '1 owner review' : `${count.toLocaleString('en-US')} owner reviews`
}

/**
 * What a NEW owner review on a product would earn, from DealHunt's points table: `review`,
 * plus `first_review_bonus` when the product has no published review yet. The one sum SoundGuys
 * does — both numbers are DealHunt's (contract phase 1c). `count` null = unknown → no bonus is
 * promised.
 */
export function reviewAward(
  points: CommunityPoints,
  count: number | null
): { points: number; bonus: boolean } {
  const bonus = count === 0 && typeof points.first_review_bonus === 'number'
  return { points: points.review + (bonus ? points.first_review_bonus ?? 0 : 0), bonus }
}

/** A picker row's second line: "3 owner reviews", "No owner review yet · first owner bonus";
 *  null when the count is unknown (a hit without a node id, an older DealHunt). */
export function pickerCountLine(
  count: number | null,
  points: CommunityPoints | null
): string | null {
  if (count === null) {
    return null
  }
  if (count > 0) {
    return reviewCountLine(count)
  }
  return points && reviewAward(points, 0).bonus
    ? 'No owner review yet · first owner bonus'
    : 'No owner review yet'
}

/** "2 more to show owner stats" — until the product page shows owner percentages. */
export function toPercentagesLine(left: number): string | null {
  return left > 0 ? `${left} more to show owner stats` : null
}

/** "8.4" from DealHunt's 0–10 SoundGuys score. */
export function sgScoreText(score: number): string {
  return (Math.round(score * 10) / 10).toFixed(1)
}

// ── thread ────────────────────────────────────────────────────────────────────

/** Shorten user text on a word boundary, with an ellipsis — for titles and descriptions. */
export function clampText(text: string, max: number): string {
  // Byte-identical to DealHunt's clamp (it registers the thread title commentHub shows; this page
  // renders it): counted in code points, so an emoji is never split into a lone surrogate, and a
  // dangling zero-width joiner (U+200D) or variation selector (U+FE0F) is stripped with the
  // punctuation (DealHunt's class `[\s.,;:!?—\-` + U+200D + U+FE0F `]`, written as an alternation here only
  // because eslint's no-misleading-character-class refuses those two in a class). Change both
  // sides together.
  const flat = text.replace(/\s+/g, ' ').trim()
  const chars = Array.from(flat)
  if (chars.length <= max) {
    return flat
  }
  const cut = chars.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  const kept = space > max * 0.6 ? cut.slice(0, space) : cut
  return `${kept.join('').replace(/(?:[\s.,;:!?—-]|\u200d|\ufe0f)+$/u, '')}…`
}

/** The thread's `<title>` — the same string DealHunt registers the reply thread under in
 *  commentHub (DealHunt retitles it after the author edits the sentence; `clampText` must stay
 *  byte-identical to DealHunt's). The thread's URL (note id) is what stays fixed and what its
 *  deep links use. */
export function threadTitle(item: CommunityFeedItem): string {
  return `“${clampText(item.note.note, 60)}” — ${item.product.name} owner review`
}

export function threadDescription(item: CommunityFeedItem): string {
  const owned = item.labels.ownership_length ? `, owner for ${item.labels.ownership_length}` : ''
  return `${item.author.handle}${owned}, on the ${item.product.name}: “${clampText(
    item.note.note,
    120
  )}”`
}

export function hubTitle(product: CommunityProduct): string {
  return `${product.name} owner reviews`
}

export function hubDescription(product: CommunityProduct, count: number): string {
  if (count === 0) {
    return `No owner review of the ${product.name} yet. Own one? Say how it holds up — how long you have had it, whether you still use it, whether you would buy it again.`
  }
  return `${reviewCountLine(count)} of the ${
    product.name
  } from people who own it — how long they have had it, whether they still use it and whether they would buy it again.`
}

export interface ThreadCell {
  label: string
  value: string
  /** `no` tints the cell (design 6: "Buy again · No" in the alert tint). */
  tone?: 'no'
}

/** How many use cases the "Mostly for" cell names before "+N". */
const MOSTLY_FOR_MAX = 2

/** "Daily use, Commute & travel +1" — the first two use-case labels and how many more. */
export function mostlyForText(labels: readonly string[]): string {
  const shown = labels.filter(Boolean).slice(0, MOSTLY_FOR_MAX)
  const rest = labels.filter(Boolean).length - shown.length
  return rest > 0 ? `${shown.join(', ')} +${rest}` : shown.join(', ')
}

/** The thread's cell strip (design 6): Owned / Still using / Mostly for / Hours per charge /
 *  What gave out / Buy again. A cell whose label DealHunt could not resolve (or did not send —
 *  `use_cases` is newer) is left out rather than printed empty; the two enrichment cells
 *  (`ThreadEnrichment`, already null for a zero or "nothing") only when present. */
export function threadCells(
  item: CommunityFeedItem,
  extra?: { gaveOut: string | null; hoursPerCharge: number | null } | null
): ThreadCell[] {
  const cells: ThreadCell[] = []
  if (item.labels.ownership_length) {
    cells.push({ label: 'Owned', value: item.labels.ownership_length })
  }
  if (item.labels.still_using) {
    cells.push({ label: 'Still using', value: item.labels.still_using })
  }
  const mostly = Array.isArray(item.labels.use_cases) ? mostlyForText(item.labels.use_cases) : ''
  if (mostly) {
    cells.push({ label: 'Mostly for', value: mostly })
  }
  if (typeof extra?.hoursPerCharge === 'number' && extra.hoursPerCharge > 0) {
    cells.push({ label: 'Hours per charge', value: hoursText(extra.hoursPerCharge) })
  }
  if (extra?.gaveOut) {
    cells.push({ label: 'What gave out', value: extra.gaveOut })
  }
  if (typeof item.note.answers.buy_again === 'boolean') {
    cells.push(
      item.note.answers.buy_again
        ? { label: 'Buy again', value: 'Yes' }
        : { label: 'Buy again', value: 'No', tone: 'no' }
    )
  }
  return cells
}

/** "Early #11" — the stored founding number (the Early Owner badge), DealHunt's. */
/** How the feed row names a review's author in prose ("Ask David", the Ask's status lines): the
 *  shown name when the member shows one, else the handle — the same word the byline prints. */
export function authorShownName(author: { handle: string; display_name?: string | null }): string {
  return author.display_name || author.handle
}

export function foundingTag(number: number): string {
  return `Early #${number.toLocaleString('en-US')}`
}

/** The feed row's score ring: its figure ("8.6") and the trigger's name; null when the product
 *  has no SoundGuys score (no ring). */
export function sgScoreRing(
  product: Pick<CommunityProduct, 'sg_score'>
): { score: number; text: string; label: string } | null {
  if (typeof product.sg_score !== 'number' || !Number.isFinite(product.sg_score)) {
    return null
  }
  const text = sgScoreText(product.sg_score)
  return {
    score: product.sg_score,
    text,
    label: `SoundGuys rating ${text} out of 10, what it means`,
  }
}

/** The score ring's overlay. PLACEHOLDER copy, the mockup's (plan §5: parked for Guillaume). */
export const SG_RATING_EXPLAINER =
  'Our score from lab measurements and listening tests, written by the SoundGuys team. It is not the owners’ call: owners say how the product compared with what they expected.'

/** Why a review sits beside this thread: the same product, the same brand (when DealHunt sends
 *  brands), else similar gear (DealHunt's `similar` = the same top category). */
export function relationLabel(
  other: CommunityProduct,
  product: CommunityProduct
): 'Same product' | 'Same brand' | 'Similar gear' {
  if (other.node_id === product.node_id) {
    return 'Same product'
  }
  if (other.brand?.slug && product.brand?.slug && other.brand.slug === product.brand.slug) {
    return 'Same brand'
  }
  return 'Similar gear'
}

/** "60% better · 30% as expected · 10% worse" from an aggregate read's three tallies — shares of
 *  the answers, zero parts left out; empty when nobody answered the read. */
export function aggregateReadLine(read: {
  better: number
  expected: number
  worse: number
}): string {
  const total = read.better + read.expected + read.worse
  if (total <= 0) {
    return ''
  }
  const part = (n: number, word: string): string | null => {
    if (n <= 0) {
      return null
    }
    return `${Math.round((n / total) * 100)}% ${word}`
  }
  return [
    part(read.better, 'better'),
    part(read.expected, 'as expected'),
    part(read.worse, 'worse'),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The reviews shown beside a thread, without the thread's own review (each review's text
 *  once per page) and without repeats. */
export function otherReviews(items: CommunityFeedItem[], exclude: string[]): CommunityFeedItem[] {
  const seen = new Set(exclude)
  return items.filter(item => {
    if (item.kind !== 'owner_review' || seen.has(item.note.id)) {
      return false
    }
    seen.add(item.note.id)
    return true
  })
}

// ── the picker's "Your gear" ──────────────────────────────────────────────────

export interface GearRow {
  /** null = unknown (a declaration made before its lookup answered): no count. */
  nodeId: string | null
  /** SoundGuys product slug — where the review form is opened. */
  slug: string
  name: string
  imageUrl: string | null
}

interface ShelfLike {
  node_id: string
  node_ids?: string[]
  node_name: string
  node_slug: string | null
  brand_name?: string | null
  brand_slug?: string | null
  image_url: string | null
  note: { status: string }
}

/**
 * "Your gear" in the picker (design 5a): only what the reader can write a review for NOW — the
 * declared product when they have no note on it, and shelf products whose note was `rejected`
 * (resubmittable). A note in flight or live (`checking`, `held_*`, `published`, `hidden`,
 * `removed`) is not waiting for a review; matched on the canonical id and its aliases. One row
 * per product. `slugOf` turns a shelf row into its SoundGuys slug (`productSlug`), injected so
 * this module stays free of the URL helpers' brand rules. Decided 2026-10-03.
 */
export function gearRows(
  declared: {
    nodeId: string | null
    href: string | null
    name: string
    imageUrl: string | null
  } | null,
  shelf: ShelfLike[],
  slugOf: (row: ShelfLike) => string | null
): GearRow[] {
  const taken = new Set<string>()
  for (const row of shelf) {
    if (row.note.status !== 'rejected') {
      taken.add(row.node_id)
      row.node_ids?.forEach(id => taken.add(id))
    }
  }
  const rows: GearRow[] = []
  const seenSlugs = new Set<string>()
  const add = (row: GearRow): void => {
    if (seenSlugs.has(row.slug) || (row.nodeId && taken.has(row.nodeId))) {
      return
    }
    seenSlugs.add(row.slug)
    rows.push(row)
  }
  const declaredSlug = declared ? slugFromProductHref(declared.href) : null
  if (declared && declaredSlug) {
    add({
      nodeId: declared.nodeId,
      slug: declaredSlug,
      name: declared.name,
      imageUrl: declared.imageUrl,
    })
  }
  for (const row of shelf) {
    const slug = slugOf(row)
    if (row.note.status === 'rejected' && slug) {
      add({ nodeId: row.node_id, slug, name: row.node_name, imageUrl: row.image_url })
    }
  }
  return rows
}

// ── categories ────────────────────────────────────────────────────────────────
//
// `/community/<path>` mirrors the taxonomy under audio: `path` is a node's `full_slug` minus
// `electronics/audio/` ("headphones/over-ear-headphones"). The list of nodes and every count on
// them are DealHunt's (`GET /api/owner/community/categories`); SoundGuys sums nothing.

const AUDIO_PREFIX = `${AUDIO_NODE_SLUG}/`

/** First segments the community already uses for other pages: never a category path. A new
 *  page directly under `/community/` adds its segment here (Directive). */
export const RESERVED_COMMUNITY_SEGMENTS: ReadonlySet<string> = new Set([
  'products',
  'members',
  'leaderboard',
  'review',
  'discussions',
])

/** At most this many segments below audio — deeper than the taxonomy goes. */
export const CATEGORY_PATH_MAX_DEPTH = 6

/** "headphones/over-ear-headphones" from a node's or a product line's `full_slug`, or null when
 *  it is not strictly under `electronics/audio` (audio itself has no community category page). */
export function communityCategoryPath(fullSlug: string | null | undefined): string | null {
  if (typeof fullSlug !== 'string' || !fullSlug.startsWith(AUDIO_PREFIX)) {
    return null
  }
  const path = fullSlug.slice(AUDIO_PREFIX.length).replace(/\/+$/, '')
  return path || null
}

/** A category's community page: `/community/<path>`, with the app's slash convention. */
export function communityCategoryHref(path: string, trailingSlash: boolean): string {
  return withSlash(`${COMMUNITY_PATH}/${path}`, trailingSlash)
}

/**
 * The category path of a `/community/[...slug]` URL — lowercased, 1–6 slug-shaped segments, the
 * first never a reserved community segment — or null. Checked BEFORE any fetch, like
 * `productSlugParam`; the page 308s other spellings to this one.
 */
export function categoryPathParam(raw: unknown): string | null {
  if (!Array.isArray(raw) || raw.length < 1 || raw.length > CATEGORY_PATH_MAX_DEPTH) {
    return null
  }
  const segments: string[] = []
  for (const part of raw) {
    if (typeof part !== 'string') {
      return null
    }
    const segment = part.toLowerCase()
    if (!/^[a-z0-9-]{1,64}$/.test(segment)) {
      return null
    }
    segments.push(segment)
  }
  return RESERVED_COMMUNITY_SEGMENTS.has(segments[0]) ? null : segments.join('/')
}

/**
 * A category path carried in a QUERY string (`?category=earbuds/wireless-earbuds` — the
 * discussions list, the writer's preset): the same grammar as `categoryPathParam`, one value,
 * edge slashes trimmed. null for anything else (absent, repeated, malformed, reserved, too deep).
 */
export function categoryPathQueryParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.replace(/^\/+|\/+$/g, '')
  return trimmed ? categoryPathParam(trimmed.split('/')) : null
}

function depthOf(fullSlug: string): number {
  return fullSlug.split('/').length
}

const AUDIO_DEPTH = depthOf(AUDIO_NODE_SLUG)

/** The node at a community path, or null (an unknown path is a 404, not an empty page). */
export function findCategory(
  categories: CommunityCategoryNode[],
  path: string
): CommunityCategoryNode | null {
  return categories.find(c => communityCategoryPath(c.full_slug) === path) ?? null
}

/** The listed nodes strictly above `fullSlug`, top level first. Segment-bounded: `headphones` is
 *  not an ancestor of `headphones-and-earbuds/…`. */
export function categoryAncestors(
  categories: CommunityCategoryNode[],
  fullSlug: string
): CommunityCategoryNode[] {
  return categories
    .filter(c => depthOf(c.full_slug) > AUDIO_DEPTH && fullSlug.startsWith(`${c.full_slug}/`))
    .sort((a, b) => depthOf(a.full_slug) - depthOf(b.full_slug))
}

/** The listed nodes exactly one segment below `fullSlug`, in DealHunt's order. */
export function categoryChildren(
  categories: CommunityCategoryNode[],
  fullSlug: string
): CommunityCategoryNode[] {
  const depth = depthOf(fullSlug) + 1
  return categories.filter(
    c => c.full_slug.startsWith(`${fullSlug}/`) && depthOf(c.full_slug) === depth
  )
}

/** The node's siblings — the children of its parent, itself included, in DealHunt's order. */
export function categorySiblings(
  categories: CommunityCategoryNode[],
  fullSlug: string
): CommunityCategoryNode[] {
  const parent = fullSlug.slice(0, fullSlug.lastIndexOf('/'))
  return parent ? categoryChildren(categories, parent) : []
}

/** The nodes directly under audio — the home page's Browse row. */
export function topLevelCategories(categories: CommunityCategoryNode[]): CommunityCategoryNode[] {
  return categoryChildren(categories, AUDIO_NODE_SLUG)
}

// ── the Browse row ────────────────────────────────────────────────────────────

/** One pill of a Browse row (`components/community/Browse`). */
export interface CategoryPill {
  key: string
  label: string
  href: string
  /** DealHunt's rolled-up count; shown only when above zero. null = no count (All, or a count
   *  DealHunt does not send yet). */
  count: number | null
  /** The page the reader is on. */
  on: boolean
}

/** Which of DealHunt's rolled-up counts a row shows: owner reviews or discussions. */
export type CategoryCountField = 'reviews' | 'discussions'

/** The owner-review rows' href builder: a category's community page, or the community home. */
export function communityRowHref(trailingSlash: boolean): (path: string | null) => string {
  return path => (path ? communityCategoryHref(path, trailingSlash) : communityHref(trailingSlash))
}

/**
 * A Browse row — the ONE parent/child rule for every category pill row (the community home and
 * category pages for owner reviews; the Discussions tab): no node → "All" (lit) and the top-level
 * categories; a node → "All" pointing at its parent (or at the top when it is top level), then its
 * children, or at a leaf its siblings with itself lit. `href(path)` builds the row's address for a
 * category path (null = the unfiltered list). Empty when there is nothing to pick from — the
 * caller renders no row then.
 */
export function categoryPillRow(
  categories: readonly CommunityCategoryNode[],
  node: CommunityCategoryNode | null,
  href: (path: string | null) => string,
  count: CategoryCountField
): CategoryPill[] {
  const pill = (c: CommunityCategoryNode, on: boolean): CategoryPill => {
    const sent = c[count]
    return {
      key: c.id,
      label: c.name,
      href: href(communityCategoryPath(c.full_slug)),
      count: typeof sent === 'number' && Number.isFinite(sent) ? sent : null,
      on,
    }
  }
  const list = [...categories]
  if (!node) {
    const top = topLevelCategories(list)
    return top.length
      ? [
          { key: 'all', label: 'All', href: href(null), count: null, on: true },
          ...top.map(c => pill(c, false)),
        ]
      : []
  }
  const children = categoryChildren(list, node.full_slug)
  const pills = children.length
    ? children.map(c => pill(c, false))
    : categorySiblings(list, node.full_slug).map(c => pill(c, c.id === node.id))
  if (!pills.length) {
    return []
  }
  const ancestors = categoryAncestors(list, node.full_slug)
  const parent = ancestors[ancestors.length - 1] ?? null
  const all: CategoryPill = {
    key: 'all',
    label: 'All',
    href: href(parent ? communityCategoryPath(parent.full_slug) : null),
    count: null,
    on: false,
  }
  return [all, ...pills]
}

/** "1 owner review", "12 owner reviews" — a count DealHunt sent. */
export function categoryReviewLine(count: number): string {
  return count === 1 ? '1 owner review' : `${count.toLocaleString('en-US')} owner reviews`
}

/** A DealHunt that could not resolve `?category=` (or predates the filter) answers the whole list —
 *  the review feed, the discussions list and the product index alike: that list must never show
 *  under a category's name. True when no category was asked, or DealHunt echoes the one asked
 *  (Constraint 9fb1344b). The product index (slice 5, #211) echoes the node itself, so its
 *  `full_slug` is read as a community path; its site-wide form (the home, no category asked)
 *  echoes `category: null` and passes here as every unfiltered list does — while a category asked
 *  and answered `null` never does. */
export function servesCategory(
  response: { category?: string | null | { full_slug?: string | null } },
  category: string | null | undefined
): boolean {
  if (!category) {
    return true
  }
  const echo = response.category
  if (echo && typeof echo === 'object') {
    return communityCategoryPath(echo.full_slug) === category
  }
  return echo === category
}

/** One step of a category trail — the shape `components/community/Crumbs` takes. */
export interface CategoryCrumb {
  label: string
  href?: string
}

/**
 * The category steps of a thread's or hub's breadcrumb, each linked to its community category
 * page (Directive 398049d8: never the catalog's category page). From the product line's
 * `full_slug` and DealHunt's list: every listed ancestor. Without either: the product's top-level
 * `category`, linked from its own `full_slug`; named without a link only when even that is absent.
 */
export function productCategoryTrail(
  product: Pick<CommunityProduct, 'category' | 'full_slug'>,
  categories: CommunityCategoryNode[] | null,
  trailingSlash: boolean
): CategoryCrumb[] {
  if (product.full_slug && categories) {
    const trail = categoryAncestors(categories, product.full_slug).flatMap(c => {
      const path = communityCategoryPath(c.full_slug)
      return path ? [{ label: c.name, href: communityCategoryHref(path, trailingSlash) }] : []
    })
    if (trail.length) {
      return trail
    }
  }
  const { category } = product
  if (!category) {
    return []
  }
  const path = communityCategoryPath(category.full_slug)
  return [
    path
      ? { label: category.name, href: communityCategoryHref(path, trailingSlash) }
      : { label: category.name },
  ]
}

// ── scoped leaderboards and the leader card ──────────────────────────────────
//
// `/community/leaderboard?category=<path>` ranks one category at any depth (the path as on the
// category pages). Every board opens on All time, at every depth (Guillaume 2026-10-04: a Month
// default opened the global board on "0 ranked" early in a month); This month and 3 months are
// explicit `?period=` views. A board is shown under a category's name only when DealHunt echoes
// that path (an older DealHunt applies a top-level slug only and answers a deeper one unfiltered),
// and under a period's pill only when DealHunt echoes that period (the pill lit is `board.period`).

/** The board's period pills, in order (Strava-style: recent race first, All time last). */
export const LEADERBOARD_PERIODS: ReadonlyArray<{ period: CommunityPeriod; label: string }> = [
  { period: 'month', label: 'This month' },
  { period: '3m', label: '3 months' },
  { period: 'all', label: 'All time' },
]

/** The leaderboard's `?period=` as a known period, or null (→ the default). Checked before any
 *  fetch: an unknown value must not reach DealHunt. */
export function leaderboardPeriodParam(raw: unknown): CommunityPeriod | null {
  return LEADERBOARD_PERIODS.find(p => p.period === raw)?.period ?? null
}

/** The leaderboard's `?category=` as a validated category path (the `categoryPathParam` rules on
 *  its segments, lowercased), or null. Checked before any fetch. */
export function leaderboardCategoryParam(raw: unknown): string | null {
  return typeof raw === 'string' && raw ? categoryPathParam(raw.split('/')) : null
}

/** True when no category was asked, or DealHunt echoes the path asked — never a wider board
 *  under a narrower name. */
export function boardServesCategory(
  board: Pick<CommunityLeaderboardResponse, 'category'>,
  path: string | null | undefined
): boolean {
  return !path || board.category === path
}

/** The node's leader for the board's period (`rows[0]`), or null when nobody is ranked: Open. */
export interface LeaderSeat {
  handle: string
  tier: CommunityTier
  points: number
}

export function leaderSeat(board: Pick<CommunityLeaderboardResponse, 'rows'>): LeaderSeat | null {
  const row = board.rows[0]
  return row ? { handle: row.handle, tier: knownTier(row.tier), points: row.points } : null
}

/** One child category's seat on a board: the child's own path, its leader (null = Open), and
 *  DealHunt's points and review count (null when an older DealHunt omits them). */
export interface ChildSeat {
  id: string
  name: string
  path: string
  handle: string | null
  points: number | null
  reviews: number | null
}

/**
 * The children of the board's node with their leaders (`category_leaders`; the top-level
 * categories on the global board). A SCOPED board without `node` comes from an older DealHunt,
 * whose `category_leaders` are the top-level categories whatever was asked: not this node's
 * children, so none.
 */
export function childSeats(
  board: Pick<CommunityLeaderboardResponse, 'category' | 'category_leaders' | 'node'>
): ChildSeat[] {
  if (board.category && board.node === undefined) {
    return []
  }
  return board.category_leaders.map(({ category, handle, points, reviews }) => ({
    id: category.id,
    name: category.name,
    path:
      communityCategoryPath(category.full_slug) ??
      (board.category ? `${board.category}/${category.slug}` : category.slug),
    handle,
    points: sentCount(points),
    reviews: sentCount(reviews),
  }))
}

/** The `fullSlug` prefix a typeahead hit must start with to be in a category: segment-bounded,
 *  so `headphones` never takes `headphones-and-earbuds`. */
export function pickerScope(path: string): string {
  return `${AUDIO_PREFIX}${path}/`
}

export function hitInScope(hit: { fullSlug?: string | null }, prefix: string): boolean {
  return typeof hit.fullSlug === 'string' && hit.fullSlug.startsWith(prefix)
}

/** "12 pts" — a figure DealHunt sent. */
export function pointsText(points: number): string {
  return `${points.toLocaleString('en-US')} pts`
}

// ── the feed's interruption (design 8b) ──────────────────────────────────────

/** "+75 each" — what a FIRST owner review on a product earns, DealHunt's `review` plus its
 *  `first_review_bonus` (`reviewAward` with no published review); null without a points table. */
export function firstReviewAwardLine(points: CommunityPoints | null | undefined): string | null {
  if (!points) {
    return null
  }
  return `+${reviewAward(points, 0).points.toLocaleString('en-US')} each`
}

/** Where the feed's interruption sits: after this many cards, once. */
export const FEED_INTERRUPT_AFTER = 5
// ── Early Owner and Founding Member (2026-10-03) ─────────────────────────────

/** "07", "14", "100" — a badge number as the hexagons print it. */
export function twoDigits(n: number): string {
  return n >= 0 && n < 10 ? `0${n}` : String(n)
}

/** "No. 03" — a Founding Member number. */
export function foundingMemberNo(n: number): string {
  return `No. ${twoDigits(n)}`
}

/** The cap DealHunt wrote into a badge's line ("#3 of 100" → 100), or null: where a page has no
 *  summary, the hexagon's "OF N" is read from DealHunt's own words or not printed. */
export function badgeCap(detail: string): number | null {
  const match = /\bof\s+([\d,]+)\b/i.exec(detail)
  const n = match ? Number(match[1].replace(/,/g, '')) : NaN
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

/** What a founding hexagon prints. Null = the badge is drawn like any other (an unearned one is
 *  the grey hexagon with its glyph). */
export type FoundingHexView =
  | { kind: 'early'; number: string | null; cap: number | null }
  | { kind: 'member'; number: string | null }

export function foundingHex(
  badge: { id: string; earned: boolean; detail: string; number?: number },
  member: { founding_number: number | null; founding_member_number?: number | null },
  /** The summary's `founding.cap` when the page read it; wins over the cap in `detail`. */
  foundingCap?: number | null
): FoundingHexView | null {
  if (!badge.earned) {
    return null
  }
  if (badge.id === 'founding_owner') {
    const n = sentCount(badge.number) ?? sentCount(member.founding_number)
    return {
      kind: 'early',
      number: n === null ? null : twoDigits(n),
      cap: earlyOwnerCap(foundingCap, badge.detail),
    }
  }
  if (badge.id === 'founding_member') {
    const n = sentCount(badge.number) ?? sentCount(member.founding_member_number)
    return { kind: 'member', number: n === null ? null : foundingMemberNo(n) }
  }
  return null
}

/** The home cards' sentences — every number is the summary's (`founding.cap`,
 *  `founding_member.rule`); SoundGuys holds no threshold. */
export function earlyOwnerAbout(cap: number): string {
  return `Among the first ${cap.toLocaleString('en-US')} signed-in owners to leave an owner review.`
}

/** "14 of 100 given" / "3 earned so far" — the home cards' pills. */
export function earlyOwnerGiven(founding: { count: number; cap: number }): string {
  return `${founding.count.toLocaleString('en-US')} of ${founding.cap.toLocaleString(
    'en-US'
  )} given`
}
export function foundingMemberEarned(count: number): string {
  return `${count.toLocaleString('en-US')} earned so far`
}

// ── the member's own page ────────────────────────────────────────────────────

/** "This is you": the reader is signed in as the member whose page this is. The one rule for
 *  every reader-specific element of the member page ("This is you", the member's own badges). */
export function isOwnMemberPage(
  me: { profile: { handle: string; claimed: boolean } },
  handle: string
): boolean {
  return me.profile.claimed && me.profile.handle.toLowerCase() === handle.toLowerCase()
}

/** The reader's standing when this is their own member page, else null. */
export function ownStanding(
  me: { profile: { handle: string; claimed: boolean }; community?: CommunityStanding } | null,
  handle: string
): CommunityStanding | null {
  return me && isOwnMemberPage(me, handle) ? me.community ?? null : null
}

/**
 * Founding Member leads "Closest to unlock" while `/me` says the member can still get it: the
 * block is there, no number is stored yet, numbers are left and DealHunt sent its progress — the
 * rule the retired "Next badge" card used (897af2f1). Own page only, after hydration.
 */
export function foundingMemberPinned(standing: CommunityStanding | null | undefined): boolean {
  const fm = standing?.founding_member
  return Boolean(
    fm && typeof fm.number !== 'number' && fm.spots_left > 0 && Array.isArray(fm.progress)
  )
}

/** The Founding Member steps (DealHunt's rule keys). */
type FoundingStepKey = Extract<CommunityProgressStep['key'], 'reviews' | 'replies' | 'helpful'>

const FOUNDING_STEP_ORDER: readonly FoundingStepKey[] = ['reviews', 'replies', 'helpful']

const FOUNDING_STEP_LABELS: Readonly<Record<FoundingStepKey, string>> = {
  reviews: 'Owner reviews',
  replies: 'Replies',
  helpful: 'Helpful received',
}

/** "3 owner reviews, 5 replies and 10 Helpful received" — from DealHunt's needs. */
function foundingRuleSentence(
  steps: ReadonlyArray<{ key: FoundingStepKey; need: number }>
): string {
  const parts = steps.map(({ key, need }) => {
    const n = need.toLocaleString('en-US')
    if (key === 'reviews') {
      return need === 1 ? '1 owner review' : `${n} owner reviews`
    }
    if (key === 'replies') {
      return need === 1 ? '1 reply' : `${n} replies`
    }
    return `${n} Helpful received`
  })
  return parts.length > 1
    ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
    : parts[0] ?? ''
}

/** The home card's sentence, from the summary's `founding_member.rule`. */
export function foundingMemberAbout(rule: CommunityFoundingMemberRule): string {
  const steps = FOUNDING_STEP_ORDER.map(key => ({ key, need: sentCount(rule[key]) ?? 0 })).filter(
    s => s.need > 0
  )
  const sentence = foundingRuleSentence(steps)
  return sentence ? `${sentence}.` : ''
}

// ── the badge popover (member page grid) ─────────────────────────────────────

/** What each badge means (`BADGE_LOOKS[id].meaning`), keyed for the callers that only want
 *  the sentence ("How points work"). Digit-free: every threshold is DealHunt's. */
export const BADGE_MEANINGS: Readonly<Record<CommunityBadgeId, string>> = Object.fromEntries(
  Object.entries(BADGE_LOOKS).map(([id, look]) => [id, look.meaning])
) as Record<CommunityBadgeId, string>

/** Every step key a badge's `progress` may carry (DealHunt widened it past the Founding Member
 *  three). A key outside this list is ignored — never drawn, never a zero. */
export type BadgeStepKey = CommunityProgressStep['key']

const BADGE_STEP_ORDER: readonly BadgeStepKey[] = [
  'reviews',
  'replies',
  'helpful',
  'categories',
  'updates',
]

/** The Founding Member three, plus the two newer counts. */
const BADGE_STEP_LABELS: Readonly<Record<BadgeStepKey, string>> = {
  ...FOUNDING_STEP_LABELS,
  categories: 'Categories reviewed',
  updates: 'Six-month updates',
}

export interface BadgeProgressRow {
  key: BadgeStepKey
  label: string
  /** DealHunt's raw counts, as sent. */
  have: number
  need: number
  /** have ≥ need — the row's ✓. */
  done: boolean
  /** "1 / 3" — DealHunt's raw `have`, never clamped. */
  value: string
  /** Bar fill, 0–100 (clamped). */
  pct: number
}

/**
 * The popover's rows from a badge's `progress`, in reviews → replies → Helpful → categories →
 * updates order. Null = no rows (an older DealHunt, a state badge, nothing readable): the
 * popover prints DealHunt's `detail` instead. A step with an unknown key, an unreadable count
 * or a zero need is dropped; a repeated key is drawn once.
 */
export function badgeProgressRows(badge: {
  progress?: ReadonlyArray<{ key: string; have: unknown; need: unknown }> | null
}): BadgeProgressRow[] | null {
  const { progress } = badge as { progress?: unknown }
  if (!Array.isArray(progress)) {
    return null
  }
  const rows: BadgeProgressRow[] = []
  BADGE_STEP_ORDER.forEach(key => {
    const step = progress.find(
      (s: unknown) => typeof s === 'object' && s !== null && (s as { key?: unknown }).key === key
    ) as { have?: unknown; need?: unknown } | undefined
    const have = sentCount(step?.have)
    const need = sentCount(step?.need)
    if (have === null || need === null || need === 0) {
      return
    }
    rows.push({
      key,
      label: BADGE_STEP_LABELS[key],
      have,
      need,
      done: have >= need,
      value: `${have.toLocaleString('en-US')} / ${need.toLocaleString('en-US')}`,
      pct: Math.min(100, Math.round((have / need) * 100)),
    })
  })
  return rows.length ? rows : null
}

/** DealHunt's `detail` line in the popover: when there are no rows, and on an earned founding
 *  badge (its "#3 of 100" number line). Null when DealHunt sent no words. */
export function badgePopoverDetail(
  badge: { id: string; earned: boolean; detail?: string | null },
  rows: readonly BadgeProgressRow[] | null
): string | null {
  const detail = typeof badge.detail === 'string' ? badge.detail.trim() : ''
  if (!detail) {
    return null
  }
  const founding = badge.id === 'founding_owner' || badge.id === 'founding_member'
  return !rows || (badge.earned && founding) ? detail : null
}

// ── the member hub's badges (Overview block, Badges page, popover) ───────────

/** A capped badge's numbers as the summary sends them: numbers given, out of DealHunt's cap. */
export interface BadgeCap {
  count: number
  cap: number
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/** "Sep 11" (UTC) — or with the year, "Sep 11, 2026". A fixed month table and UTC fields, never
 *  `Intl`: the label is in the shared-cached HTML, so the server's and the browser's ICU must not
 *  be able to disagree (Constraint bb38c2ea). Null when absent or unreadable. */
export function utcDay(iso: string | null | undefined, withYear = false): string | null {
  if (typeof iso !== 'string' || !iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  const day = `${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`
  return withYear ? `${day}, ${date.getUTCFullYear()}` : day
}

/** What the badge's status reads like: green earned, amber scarce, blue moving, grey idle. */
export type BadgeStatusTone = 'earned' | 'limited' | 'progress' | 'idle'

export interface BadgeStatus {
  text: string
  tone: BadgeStatusTone
}

/** The own-page caption of an unearned Conversation Starter whose discussion waits for a reply
 *  from someone else (`starterTileWaiting`). */
export const BADGE_WAITING_LABEL = 'Waiting for a reply'

/** Numbers still to give: cap − count, never below zero. */
export function badgeLeft(cap: BadgeCap): number {
  return Math.max(0, cap.cap - cap.count)
}

/**
 * The one short status under a badge (design 3d) — never cut off, so it is built to be short:
 *
 * 1. earned → "Earned Sep 11" (green); "Earned" when DealHunt sends no date (`long_haul_owner`,
 *    `top3_month` never have one);
 * 2. (own page) a Conversation Starter waiting for a reply → "Waiting for a reply" (blue);
 * 3. a capped badge WITHOUT progress rows (Early Owner, Conversation Starter, First Responder —
 *    the summary sent `{count, cap}`) → "85 left" (amber), "All taken" (grey) once none is left.
 *    A capped badge WITH rows (Founding Member) reads its steps; its "N left" is the popover's
 *    rarity line (design 3d, Guillaume 2026-10-05);
 * 4. DealHunt's `opens_at` (not sent yet) → "Opens Mar 11" (grey) — SoundGuys never computes
 *    the date;
 * 5. something done: several rows → "0 of 3 steps" / "1 of 3 steps" (blue) — any count above
 *    zero, even before a step completes; one row → "2 of 3" (blue);
 * 6. every count at zero, or nothing to read → "Start" (grey).
 *
 * Every figure is the payload's (`progress`, the summary's cap); SoundGuys holds none.
 */
export function badgeStatus(
  badge: {
    earned: boolean
    earned_at?: string | null
    opens_at?: string | null
    progress?: ReadonlyArray<{ key: string; have: unknown; need: unknown }> | null
  },
  cap?: BadgeCap | null,
  waiting = false
): BadgeStatus {
  if (badge.earned) {
    const day = utcDay(badge.earned_at)
    return { text: day ? `Earned ${day}` : 'Earned', tone: 'earned' }
  }
  if (waiting) {
    return { text: BADGE_WAITING_LABEL, tone: 'progress' }
  }
  const rows = badgeProgressRows(badge)
  if (cap && !rows) {
    const left = badgeLeft(cap)
    return left > 0
      ? { text: `${left.toLocaleString('en-US')} left`, tone: 'limited' }
      : { text: 'All taken', tone: 'idle' }
  }
  const opens = utcDay(badge.opens_at)
  if (opens) {
    return { text: `Opens ${opens}`, tone: 'idle' }
  }
  const started = rows !== null && rows.some(row => row.have > 0)
  if (rows && started && rows.length > 1) {
    const done = rows.filter(row => row.done).length
    return { text: `${done} of ${rows.length} steps`, tone: 'progress' }
  }
  if (rows && started) {
    const [row] = rows
    return {
      text: `${row.have.toLocaleString('en-US')} of ${row.need.toLocaleString('en-US')}`,
      tone: 'progress',
    }
  }
  return { text: 'Start', tone: 'idle' }
}

/** How far a badge is, 0–1: the mean of its rows' `min(1, have / need)` — each step weighs the
 *  same, so "3 / 3, 0 / 5, 0 / 10" is a third of the way. 0 without rows. */
export function badgeRatio(rows: readonly BadgeProgressRow[] | null): number {
  if (!rows || rows.length === 0) {
    return 0
  }
  return rows.reduce((sum, row) => sum + Math.min(1, row.have / row.need), 0) / rows.length
}

/** A capped badge whose every number is given: nobody can earn it any more. */
export function badgeSoldOut(
  id: CommunityBadgeId,
  caps: Partial<Record<CommunityBadgeId, BadgeCap>> | null | undefined
): boolean {
  const cap = caps?.[id]
  return cap ? badgeLeft(cap) === 0 : false
}

export interface ClosestOptions {
  /** How many cards (3 on the Overview). */
  limit?: number
  /** Badges that lead, in this order, while unearned and not sold out — own page: Founding
   *  Member while `/me` says the member can still get it, then a Conversation Starter waiting
   *  for a reply (DealHunt sends it no progress). */
  pinned?: readonly CommunityBadgeId[]
  /** The summary's caps (`badgeFacts`): a sold-out badge is never suggested. */
  caps?: Partial<Record<CommunityBadgeId, BadgeCap>> | null
}

/**
 * "Closest to unlock" (design 2c): up to `limit` locked badges that carry DealHunt's progress
 * rows, the furthest along first (`badgeRatio`), ties in definition order (`badgeOrder`) — never
 * DealHunt's array order, so the block cannot shuffle between reads. A badge with nothing done
 * yet only fills a slot the started ones leave empty; a sold-out one never shows; `pinned` ones
 * lead.
 */
export function closestBadges<
  T extends {
    id: CommunityBadgeId
    earned: boolean
    progress?: ReadonlyArray<{ key: string; have: unknown; need: unknown }> | null
  }
>(
  badges: readonly T[],
  { limit = 3, pinned = [], caps = null }: ClosestOptions = {}
): Array<{
  badge: T
  ratio: number
}> {
  const open = badges.filter(badge => !badge.earned && !badgeSoldOut(badge.id, caps))
  const entry = (badge: T): { badge: T; ratio: number } => ({
    badge,
    ratio: badgeRatio(badgeProgressRows(badge)),
  })
  const head = pinned
    .map(id => open.find(badge => badge.id === id))
    .filter((badge): badge is T => badge !== undefined)
    .map(entry)
  const ranked = open
    .filter(badge => !pinned.includes(badge.id) && badgeProgressRows(badge) !== null)
    .map(entry)
    .sort((a, b) => b.ratio - a.ratio || badgeOrder(a.badge.id) - badgeOrder(b.badge.id))
  const started = ranked.filter(e => e.ratio > 0)
  const rest = ranked.filter(e => e.ratio === 0)
  return [...head, ...started, ...rest].slice(0, limit)
}

/** "1 of 3 steps done" — the popover's summary line for a badge with several rows; null for one
 *  row or none (the row says it). */
export function badgeStepsLine(rows: readonly BadgeProgressRow[] | null): string | null {
  if (!rows || rows.length < 2) {
    return null
  }
  return `${rows.filter(row => row.done).length} of ${rows.length} steps done`
}

/** Owner-only, in an earned badge's popover: the closest locked badge in the same group —
 *  "Next in Reviews: Review Trio · 1 / 3" — or null when the group has none with progress. */
export function nextInGroup<
  T extends {
    id: CommunityBadgeId
    earned: boolean
    progress?: ReadonlyArray<{ key: string; have: unknown; need: unknown }> | null
  }
>(
  badges: readonly T[],
  id: CommunityBadgeId,
  /** The summary's caps: a sold-out badge is never "next". */
  caps: Partial<Record<CommunityBadgeId, BadgeCap>> | null = null
): { group: string; name: string; value: string } | null {
  const { group } = BADGE_LOOKS[id]
  const [next] = closestBadges(
    badges.filter(badge => badge.id !== id && BADGE_LOOKS[badge.id].group === group),
    { limit: 1, caps }
  )
  if (!next) {
    return null
  }
  const rows = badgeProgressRows(next.badge) as BadgeProgressRow[]
  return {
    group: badgeGroupLabel(group),
    name: BADGE_LOOKS[next.badge.id].name,
    value: rows.length === 1 ? rows[0].value : (badgeStepsLine(rows) as string),
  }
}

/** What a progress row's link does (owner only — a visitor never sees a locked badge):
 *  `review` opens the owner picker (the same entry as the You card);
 *  `reply` leads to the discussions waiting for an answer; `update` to the member's own reviews;
 *  `others` has no link — other readers give it. */
export type BadgeStepAction = 'review' | 'reply' | 'update' | 'others'

export function badgeStepAction(key: BadgeStepKey): BadgeStepAction {
  switch (key) {
    case 'reviews':
    case 'categories':
      return 'review'
    case 'replies':
      return 'reply'
    case 'updates':
      return 'update'
    default:
      return 'others'
  }
}

/** The one act a capped badge without progress rows asks for (the popover's primary CTA):
 *  Early Owner → an owner review; Conversation Starter → a new discussion; First Responder → a
 *  discussion waiting for an answer. Null for any other badge. */
export type BadgeLimitedAction = 'review' | 'start' | 'answer'

export function badgeLimitedAction(id: CommunityBadgeId): BadgeLimitedAction | null {
  switch (id) {
    case 'founding_owner':
      return 'review'
    case 'conversation_starter':
      return 'start'
    case 'first_responder':
      return 'answer'
    default:
      return null
  }
}

/** The single-action scarcity bar (design 3b): "85 of 100 left", "15 given", and the FILL is
 *  the REMAINING share (the design filled the given share; what is left is the point). */
export function badgeScarcity(cap: BadgeCap): { left: string; given: string; pct: number } {
  const left = badgeLeft(cap)
  return {
    left: `${left.toLocaleString('en-US')} of ${cap.cap.toLocaleString('en-US')} left`,
    given: `${cap.count.toLocaleString('en-US')} given`,
    pct: Math.max(0, Math.min(100, Math.round((left / cap.cap) * 100))),
  }
}

/** "12 members have this" — a capped badge's count is the members holding a number. */
export function badgeHoldersLine(cap: BadgeCap): string {
  return cap.count === 1
    ? '1 member has this'
    : `${cap.count.toLocaleString('en-US')} members have this`
}

/** The capped badge's footer: "12 members have this · 85 left · can’t be earned once they’re
 *  gone", or "All 100 taken" — every number the summary's. */
export function badgeRarityLine(cap: BadgeCap): string {
  const left = badgeLeft(cap)
  if (left === 0) {
    return `All ${cap.cap.toLocaleString('en-US')} taken`
  }
  return `${badgeHoldersLine(cap)} · ${left.toLocaleString(
    'en-US'
  )} left · can’t be earned once they’re gone`
}

/** The popover's eyebrow: the group, with DealHunt's cap on a capped badge ("Limited · 100
 *  total"). */
export function badgeEyebrow(id: CommunityBadgeId, cap?: BadgeCap | null): string {
  const label = badgeGroupLabel(BADGE_LOOKS[id].group)
  return cap ? `${label} · ${cap.cap.toLocaleString('en-US')} total` : label
}

/** The badges a reader is shown: the member themself sees every one; anyone else the earned ones
 *  only (design 2d — a page of grey hexagons reads as "this person did nothing"). */
export function shownBadges<T extends { earned: boolean }>(
  badges: readonly T[],
  own: boolean
): T[] {
  return own ? [...badges] : badges.filter(badge => badge.earned)
}

/** The badges of one group, in definition order. */
export function badgesInGroup<T extends { id: CommunityBadgeId }>(
  badges: readonly T[],
  group: BadgeGroup
): T[] {
  return badges
    .filter(badge => BADGE_LOOKS[badge.id].group === group)
    .sort((a, b) => badgeOrder(a.id) - badgeOrder(b.id))
}

/** A member's Badges page: `/community/members/<handle>/badges`. */
export function memberBadgesHref(handle: string, trailingSlash: boolean): string {
  return memberHubHref(handle, trailingSlash, 'badges')
}

// ── the member hover card (2026-10-04) ───────────────────────────────────────

/** The card route's path for a handle, with the app's trailing-slash rule (an API route is
 *  slashed like every other path when the app is — otherwise each hover pays a 308). */
export function memberCardApiPath(handle: string, trailingSlash: boolean): string {
  return withSlash(
    `/api/community/members/${encodeURIComponent(handle.toLowerCase())}`,
    trailingSlash
  )
}

/** "EB" from "Earbuds", "HP" from "Over-ear headphones"/"Headphones"; product initials else. */
export function tileLetters(review: {
  name: string
  category?: { name?: string | null } | null
}): string {
  const source = review.category?.name || review.name
  const words = source.split(/[\s-]+/).filter(Boolean)
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
  }
  const w = words[0] ?? ''
  if (/phones$/i.test(w)) {
    return `${w.charAt(0)}P`.toUpperCase()
  }
  if (/buds$/i.test(w)) {
    return `${w.charAt(0)}B`.toUpperCase()
  }
  return w.slice(0, 2).toUpperCase()
}

/** "Owns and reviewed" rows on the member card (Amendment B.3). */
const CARD_GEAR = 2

/** The card's gear rows: the member's reviews, most-read first (ties keep DealHunt's order),
 *  trimmed to name / category / ownership / photo / buy-again / thumbnail. A row without a
 *  product name is skipped. */
function memberCardGear(items: CommunityFeedItem[]): CommunityMemberCardGear[] {
  return items
    .filter(it => typeof it?.product?.name === 'string' && it.product.name !== '')
    .map((it, index) => ({ it, index, views: sentCount(it.note?.views_total) ?? 0 }))
    .sort((a, b) => b.views - a.views || a.index - b.index)
    .slice(0, CARD_GEAR)
    .map(({ it }) => ({
      name: it.product.name,
      category: it.product.category?.name || null,
      ownership: it.labels?.ownership_length || null,
      photo: (sentCount(it.note?.image_count) ?? 0) > 0 || it.note?.photo_status === 'verified',
      buy_again:
        typeof it.note?.answers?.buy_again === 'boolean' ? it.note.answers.buy_again : null,
      image_url: safeImageUrl(it.product.image_url),
    }))
}

/**
 * DealHunt's member read trimmed to what the hover card prints. Every figure is passed through
 * as sent; the one sum is `readers_total` (Σ `views_total`, the Stats tab's "readers all time" —
 * public since contract §0.1). `reviews[]` (text) and every per-review count stay out: the gear
 * rows carry labels only. Badges: earned ones this build knows, in `knownBadges` order; the total
 * counts every known one. Derived from the ONE member read the route already makes (4d4010ca).
 */
export function memberCardProjection(member: CommunityMemberResponse): CommunityMemberCardResponse {
  const known = knownBadges(member.badges ?? [])
  // The Early Owner mini hexagon's cap, from DealHunt's own badge line (the card route reads no
  // summary); absent = the digit-free mark.
  const earlyOwner = known.find(b => b.id === 'founding_owner')
  const foundingCap = earlyOwner ? earlyOwnerCap(null, earlyOwner.detail) : null
  const items = Array.isArray(member.reviews) ? member.reviews : []
  const repliesAll = sentCount(member.counts?.replies_all)
  const next = member.next_tier
  const nextTier =
    next && typeof next.id === 'string' && sentCount(next.points_to_go) !== null
      ? { id: knownTier(next.id), points_to_go: next.points_to_go }
      : null
  return {
    handle: member.handle,
    tier: knownTier(member.tier),
    points: member.points,
    founding_number: sentCount(member.founding_number),
    founding_member_number: sentCount(member.founding_member_number),
    counts: {
      reviews: member.counts.reviews,
      products: member.counts.products,
      ...(repliesAll !== null ? { replies_all: repliesAll } : {}),
    },
    rank_all: sentCount(member.rank_all) || null,
    next_tier: nextTier,
    readers_total: items.reduce((n, it) => n + (sentCount(it?.note?.views_total) ?? 0), 0),
    gear: memberCardGear(items),
    badges: known
      .filter(b => b.earned)
      .map(b => {
        const badge: { id: CommunityBadgeId; earned_at?: string } = { id: b.id }
        if (typeof b.earned_at === 'string') {
          badge.earned_at = b.earned_at
        }
        return badge
      }),
    badges_total: known.length,
    joined_at: typeof member.joined_at === 'string' ? member.joined_at : null,
    ...(foundingCap !== null ? { founding_cap: foundingCap } : {}),
  }
}

/** Amendment B.3, a member without a review: "10 pts · 1 reply in discussions" — the replies part
 *  only when DealHunt sent a count above 0. */
export function memberCardEmptyLine(
  card: Pick<CommunityMemberCardResponse, 'points' | 'counts'>
): string {
  const replies = sentCount(card.counts?.replies_all) ?? 0
  const pts = pointsText(card.points)
  return replies > 0
    ? `${pts} · ${replies.toLocaleString('en-US')} ${
        replies === 1 ? 'reply' : 'replies'
      } in discussions`
    : pts
}

/** Amendment B.3, a gear row's meta: "Headphones · 6–12 months · with photo" — only the parts
 *  DealHunt sent; '' when none. */
export function memberCardGearMeta(gear: CommunityMemberCardGear): string {
  return [gear.category, gear.ownership, gear.photo ? 'with photo' : null]
    .filter((p): p is string => Boolean(p))
    .join(' · ')
}

/** Amendment B.3, the rank column: "#27", "—" when unranked. */
export function memberCardRank(card: Pick<CommunityMemberCardResponse, 'rank_all'>): string {
  return card.rank_all ? `#${card.rank_all.toLocaleString('en-US')}` : '—'
}
